/**
 * President Jewellers — PayPal Pay Later message variant-refresh
 *
 * When a customer switches variant on a PDP, the price element's text
 * is updated by Eurus's variant-select.js. This script observes those
 * price mutations, updates each [data-pp-message][data-pp-placement="product"]
 * within the same product section to reflect the new variant price, and
 * calls paypal.Messages({...}).render(el) to re-render the message in place.
 *
 * Scope: only product placements (data-pp-placement="product"). Cart
 * placements use cart.total_price and are re-rendered by full cart refresh.
 *
 * Loaded after the PayPal SDK script in layout/theme.liquid, async so it
 * runs once paypal.Messages is available (waits via a short poll).
 */
(function () {
  'use strict';

  if (typeof window === 'undefined') return;

  // Parse a price string like "£1,234.00" or "$1234" into a number in major units.
  function parsePrice(text) {
    if (!text) return null;
    var cleaned = String(text).replace(/[^\d.,-]/g, '');
    // If both comma and dot appear and dot comes last, treat comma as thousands sep
    var lastComma = cleaned.lastIndexOf(',');
    var lastDot = cleaned.lastIndexOf('.');
    if (lastComma > -1 && lastDot > -1) {
      if (lastDot > lastComma) {
        cleaned = cleaned.replace(/,/g, '');
      } else {
        cleaned = cleaned.replace(/\./g, '').replace(',', '.');
      }
    } else if (lastComma > -1) {
      // Only comma — could be decimal (EU) or thousands. Heuristic: 2 digits after = decimal.
      var afterComma = cleaned.length - lastComma - 1;
      if (afterComma === 2) {
        cleaned = cleaned.replace(',', '.');
      } else {
        cleaned = cleaned.replace(/,/g, '');
      }
    }
    var n = parseFloat(cleaned);
    return isFinite(n) && n > 0 ? n : null;
  }

  // Wait until paypal.Messages is available on window.
  function whenPaypalReady(cb, attempts) {
    attempts = attempts || 0;
    if (typeof window.paypal !== 'undefined' && window.paypal.Messages) {
      cb();
      return;
    }
    if (attempts > 100) return; // ~10s ceiling
    setTimeout(function () { whenPaypalReady(cb, attempts + 1); }, 100);
  }

  // Read the eligibility range from data attributes on the element (defaults match Pay in 3 UK: £20–£3,000).
  function eligibilityFor(el) {
    var min = parseFloat(el.getAttribute('data-pj-pp-min'));
    var max = parseFloat(el.getAttribute('data-pj-pp-max'));
    return {
      min: isFinite(min) ? min : 20,
      max: isFinite(max) ? max : 3000
    };
  }

  // Re-render a single [data-pp-message] element with the latest amount.
  function rerenderMessage(el) {
    if (!el || typeof window.paypal === 'undefined' || !window.paypal.Messages) return;
    var amount = parseFloat(el.getAttribute('data-pp-amount'));
    if (!isFinite(amount) || amount <= 0) return;
    try {
      window.paypal.Messages({
        amount: amount,
        placement: el.getAttribute('data-pp-placement') || 'product',
        style: {
          layout: el.getAttribute('data-pp-style-layout') || 'text',
          logo: { type: el.getAttribute('data-pp-style-logo-type') || 'inline' },
          text: { color: el.getAttribute('data-pp-style-text-color') || 'black' }
        }
      }).render(el);
    } catch (err) {
      // swallow — PayPal SDK occasionally throws on race conditions; next mutation will retry
    }
  }

  // Apply eligibility: hide if out of range, show + re-render if in range.
  function applyMessageState(el, amount) {
    var range = eligibilityFor(el);
    if (amount >= range.min && amount <= range.max) {
      el.style.display = '';
      el.setAttribute('data-pp-amount', amount.toFixed(2));
      rerenderMessage(el);
    } else {
      el.style.display = 'none';
    }
  }

  // Find the enclosing product section and update its product placements.
  function refreshMessagesNear(priceEl, newAmount) {
    var scope = priceEl.closest('main, .x-section, section, body') || document;
    var msgs = scope.querySelectorAll('[data-pp-message][data-pp-placement="product"]');
    if (msgs.length === 0) {
      msgs = document.querySelectorAll('[data-pp-message][data-pp-placement="product"]');
    }
    msgs.forEach(function (msg) {
      applyMessageState(msg, newAmount);
    });
  }

  // Set up observers on every #price-* element on the page.
  function setupObservers() {
    var priceEls = document.querySelectorAll('[id^="price-"]');
    if (priceEls.length === 0) return;

    priceEls.forEach(function (priceEl) {
      if (priceEl.__pjPaypalObserved) return;
      priceEl.__pjPaypalObserved = true;

      var lastAmount = parsePrice(priceEl.textContent);

      var observer = new MutationObserver(function () {
        var amount = parsePrice(priceEl.textContent);
        if (amount === null) return;
        if (amount === lastAmount) return;
        lastAmount = amount;
        refreshMessagesNear(priceEl, amount);
      });

      observer.observe(priceEl, {
        childList: true,
        subtree: true,
        characterData: true
      });
    });
  }

  // Initial setup: wait for SDK, then attach observers.
  function init() {
    whenPaypalReady(function () {
      setupObservers();
      // Re-scan periodically for dynamically inserted price elements
      // (e.g. quick-view, AJAX-loaded sections). Light touch.
      var rescans = 0;
      var rescanInterval = setInterval(function () {
        rescans++;
        setupObservers();
        if (rescans > 6) clearInterval(rescanInterval); // 6 × 2s = 12s after load
      }, 2000);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
